import { DashboardLayout } from "@/components/dashboard-layout";
import { createFileRoute, useNavigate, useMatches } from "@tanstack/react-router";
import { Outlet } from "@tanstack/react-router";
import type { SubmenuItem } from "@/components/submenu";
import type { DropdownItem } from "@/components/dropdown";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BriefcaseBusiness, Tags, FolderTree } from "lucide-react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useEffect, useState, useMemo } from "react";
import {
  businessCategoriesQueryOptions,
  businessesQueryOptions,
  businessTypesQueryOptions,
  deleteBusinessFn,
  deleteBusinessTypeFn,
  deleteBusinessCategoryFn,
} from "@/server/business-directory";
import { toast } from "sonner";
import { CategoryReadable, TypeReadable } from "@/lib/mma/types.gen";
import { checkIfUserMayWrite, checkIfUserMayDelete, getPermissionRestraintMessage } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/business-directory")({
  component: RouteComponent,
  loader: ({ context }) => {
    return {
      crumb: "Business Directory",
      title: "Business Directory",
      userProfileId: (context as any).user?.profile,
      accessControl: (context as any).user?.accessControl,
      user: (context as any).user,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = Route.useLoaderData();
  usePermissionRedirect({ user, permission: "BUSINESS_DIRECTORY" });
  const matches = useMatches();
  const isRootBusinessDirectory = matches[matches.length - 1].fullPath === "/business-directory";
  const { userProfileId, accessControl } = Route.useLoaderData();
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "BUSINESS_DIRECTORY");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "BUSINESS_DIRECTORY");

  // State for confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    item?: SubmenuItem;
    type?: "business" | "type" | "category";
    affectedBusinessNames?: string[];
    loadingAffected?: boolean;
  }>({ open: false });

  const categoriesQuery = useQuery(businessCategoriesQueryOptions);
  const categories = categoriesQuery.data;
  const businessesQuery = useQuery(businessesQueryOptions);
  const businesses = businessesQuery.data;
  const typesQuery = useQuery(businessTypesQueryOptions);
  const types = typesQuery.data;

  useEffect(() => {
    if (categoriesQuery.isError) {
      const message =
        categoriesQuery.error instanceof Error
          ? categoriesQuery.error.message
          : "Failed to load categories. Please try again later.";
      toast.error(message);
    }
  }, [categoriesQuery.isError, categoriesQuery.error]);

  useEffect(() => {
    if (businessesQuery.isError) {
      const message =
        businessesQuery.error instanceof Error
          ? businessesQuery.error.message
          : "Failed to load businesses. Please try again later.";
      toast.error(message);
    }
  }, [businessesQuery.isError, businessesQuery.error]);

  useEffect(() => {
    if (typesQuery.isError) {
      const message =
        typesQuery.error instanceof Error ? typesQuery.error.message : "Failed to load types. Please try again later.";
      toast.error(message);
    }
  }, [typesQuery.isError, typesQuery.error]);

  const deleteBusinessMutation = useMutation({
    mutationFn: deleteBusinessFn,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["businesses"] });
      setConfirmDialog({ open: false });
      navigate({ to: "/business-directory" });
    },
    onError: () => {
      setConfirmDialog({ open: false });
    },
  });

  const deleteBusinessTypeMutation = useMutation({
    mutationFn: deleteBusinessTypeFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businessTypes"] });
      setConfirmDialog({ open: false });
      navigate({ to: "/business-directory" });
    },
    onError: () => {
      setConfirmDialog({ open: false });
    },
  });

  const deleteBusinessCategoryMutation = useMutation({
    mutationFn: deleteBusinessCategoryFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businessCategories"] });
      setConfirmDialog({ open: false });
      navigate({ to: "/business-directory" });
    },
    onError: () => {
      setConfirmDialog({ open: false });
    },
  });

  const deleteType = (id: number) => {
    const deletePromise = deleteBusinessTypeMutation.mutateAsync({ data: { id } });
    toast.promise(deletePromise, {
      loading: "Deleting business type...",
      success: "Business type deleted successfully",
      error: (error) => error.message || "Failed to delete business type. Please try again.",
    });
  };

  const deleteCategory = (id: number) => {
    const deletePromise = deleteBusinessCategoryMutation.mutateAsync({ data: { id } });
    toast.promise(deletePromise, {
      loading: "Deleting business category...",
      success: "Business category deleted successfully",
      error: (error) => error.message || "Failed to delete business category. Please try again.",
    });
  };

  const deleteBusiness = (id: number) => {
    const deletePromise = deleteBusinessMutation.mutateAsync({ data: { id } });
    toast.promise(deletePromise, {
      loading: "Deleting business...",
      success: "Business deleted successfully",
      error: (error) => error.message || "Failed to delete business. Please try again.",
    });
  };

  const handleDropdownAction = (item: SubmenuItem, functionType?: string) => {
    if (functionType === "delete") {
      if (!mayDelete) {
        toast.error(getPermissionRestraintMessage("delete business directory items"));
        return;
      }
      const dropdownItem = item as any;
      const parent = dropdownItem.parent;

      let itemType: "business" | "type" | "category";
      const itemId = item.id.toString();

      if (itemId.includes("delete-business-")) {
        itemType = "business";
      } else if (itemId.includes("delete-type-")) {
        itemType = "type";
      } else if (itemId.includes("delete-category-")) {
        itemType = "category";
      } else {
        return;
      }

      const parentItem: SubmenuItem = {
        id: parent.id,
        name: parent.name,
      };

      // TODO: Re-enable affected businesses check once backend is ready
      // if (itemType === "category" || itemType === "type") {
      //   const allIds = ((businesses as any[]) ?? []).map((b: any) => b.id).filter(Boolean) as number[];
      //   Promise.all(allIds.map((id) => queryClient.fetchQuery(businessQueryOptions(id))))
      //     .then((allBusinesses) => {
      //       const parentIdNum = Number(parent.id);
      //       const affected = allBusinesses
      //         .filter((b) => (itemType === "category" ? b.categoryId === parentIdNum : b.typeId === parentIdNum))
      //         .map((b) => b.name ?? `Business ${b.id}`);
      //       setConfirmDialog({
      //         open: true,
      //         item: parentItem,
      //         type: itemType,
      //         loadingAffected: false,
      //         affectedBusinessNames: affected.length > 0 ? affected : undefined,
      //       });
      //     })
      //     .catch(() => {
      //       setConfirmDialog({
      //         open: true,
      //         item: parentItem,
      //         type: itemType,
      //         loadingAffected: false,
      //         affectedBusinessNames: undefined,
      //       });
      //     });
      // } else {
      //   setConfirmDialog({
      //     open: true,
      //     item: parentItem,
      //     type: itemType,
      //   });
      // }
      setConfirmDialog({
        open: true,
        item: parentItem,
        type: itemType,
      });
    } else if (functionType === "create-child") {
      const dropdownItem = item as any;
      const parent = dropdownItem.parent;

      // Navigate to create new business with parent ID
      navigate({
        to: "/business-directory/businesses",
        search: { id: "new", parentId: parent.id },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (!confirmDialog.item || !confirmDialog.type) return;

    const itemId = Number(confirmDialog.item.id);

    switch (confirmDialog.type) {
      case "business":
        deleteBusiness(itemId);
        break;
      case "type":
        deleteType(itemId);
        break;
      case "category":
        deleteCategory(itemId);
        break;
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ open: false });
  };

  const createDropdownItems = (itemId: string | number, itemName: string, itemType: string): DropdownItem[] => [
    ...(mayDelete
      ? [
          {
            id: `delete-${itemType}-${itemId}`,
            name: "Delete",
            onClick: "delete",
            color: "destructive",
            parent: { name: itemName, id: itemId.toString() },
          },
        ]
      : []),
  ];

  const createBusinessDropdownItems = (itemId: number, itemName: string): DropdownItem[] => [
    {
      id: `edit-business-${itemId}`,
      name: "Edit",
      href: `/business-directory/businesses?id=${itemId}`,
      color: "default",
    },
    {
      id: `organisations-${itemId}`,
      name: "Organisations",
      href: `/business-directory/businesses/organisations?id=${itemId}`,
      color: "default",
    },
    ...(mayWrite
      ? [
          {
            id: `create-child-business-${itemId}`,
            name: "Create Child",
            onClick: "create-child",
            color: "default",
            parent: { name: itemName, id: itemId },
          },
        ]
      : []),
    ...(mayDelete
      ? [
          {
            id: `delete-business-${itemId}`,
            name: "Delete",
            onClick: "delete",
            color: "destructive",
            parent: { name: itemName, id: itemId },
          },
        ]
      : []),
  ];

  const buildTree = (items: any[]) => {
    const byId = new Map<string | number, any>();
    const roots: any[] = [];

    // First pass: create all nodes
    for (const item of items) {
      if (!byId.has(item.id)) {
        const node = {
          ...item,
          href: `/business-directory/businesses?id=${item.id}`,
          dropdown: createBusinessDropdownItems(item.id, item.name) as any,
          isParent: false,
          clickableParent: false,
          children: [],
        };
        byId.set(item.id, node);
      }
    }

    // Second pass: build parent-child relationships
    for (const item of items) {
      const node = byId.get(item.id);
      if (!node) continue;

      if (!item.parentId) {
        roots.push(node);
      } else {
        const parent = byId.get(item.parentId);
        if (!parent) {
          continue;
        }
        parent.children = parent.children || [];
        parent.children.push(node);
        parent.isParent = true;
        parent.clickableParent = true;
      }
    }

    roots.sort((a, b) => {
      const aHasChildren = a.isParent && a.children && a.children.length > 0;
      const bHasChildren = b.isParent && b.children && b.children.length > 0;

      if (aHasChildren !== bHasChildren) {
        return aHasChildren ? -1 : 1;
      }

      return (a.name || "").localeCompare(b.name || "");
    });

    for (const node of byId.values()) {
      if (node.children && node.children.length > 0) {
        node.children.sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
      }
    }

    return roots;
  };

  const submenuItems: SubmenuItem[] = useMemo(() => {
    if (!businesses || !types || !categories) return [];
    return [
      {
        id: "businesses",
        name: "Businesses",
        href: "/business-directory",
        icon: <BriefcaseBusiness className="h-4 w-4" />,
        isParent: true,
        children: buildTree(businesses as any[]),
      },
      {
        id: "types",
        name: "Types",
        href: "/business-directory/types",
        icon: <Tags className="h-4 w-4" />,
        isParent: true,
        children: (types as TypeReadable[])
          ?.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
          .map((type) => ({
            id: type.id,
            name: type.name,
            href: `/business-directory/types?id=${type.id}`,
            dropdown: createDropdownItems(type.id as number, type.name as string, "type") as any,
          })),
      },
      {
        id: "categories",
        name: "Categories",
        href: "/business-directory/categories",
        icon: <FolderTree className="h-4 w-4" />,
        isParent: true,
        children: (categories as CategoryReadable[])
          ?.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
          .map((category) => ({
            id: category.id,
            name: category.name,
            href: `/business-directory/categories?id=${category.id}`,
            dropdown: createDropdownItems(category.id as number, category.name as string, "category") as any,
          })),
      },
    ];
  }, [businesses, types, categories]);

  const createNewButtons: { label: string; href: string; params?: any }[] = [
    { label: "+ Create New Business", href: "/business-directory/businesses?id=new" },
    { label: "+ Create New Type", href: "/business-directory/types?id=new" },
    { label: "+ Create New Category", href: "/business-directory/categories?id=new" },
  ];

  const submenu = {
    items: submenuItems,
    title: "Business Directory",
    active: true,
    createNew: mayWrite ? createNewButtons : undefined,
    placeholder: "Search business directory...",
    onClick: handleDropdownAction,
    itemsLoading: categoriesQuery.isLoading || businessesQuery.isLoading || typesQuery.isLoading,
  };

  const isDeleting =
    deleteBusinessMutation.isPending ||
    deleteBusinessTypeMutation.isPending ||
    deleteBusinessCategoryMutation.isPending;

  return (
    <>
      <DashboardLayout submenu={submenu}>
        {isRootBusinessDirectory ? (
          <Card>
            <CardContent>
              <p className="text-md pb-20 pt-15 text-center">
                Please select an item from the submenu to view its details or manage it.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Item"
        description={
          confirmDialog.item
            ? `Are you sure you want to delete "${confirmDialog.item.name}"? This action cannot be undone.`
            : "Are you sure you want to delete this item? This action cannot be undone."
        }
        subtitle={
          confirmDialog.loadingAffected ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          ) : confirmDialog.affectedBusinessNames && confirmDialog.affectedBusinessNames.length > 0 ? (
            `The following ${
              confirmDialog.affectedBusinessNames.length === 1
                ? "business"
                : `${confirmDialog.affectedBusinessNames.length} businesses`
            } will also be deleted:`
          ) : undefined
        }
        affectedItems={confirmDialog.affectedBusinessNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isDeleting}
        showConfirmButton={true}
      />
    </>
  );
}
